import { isFeatureEnabled } from '../utils/feature-flags';

const FEATURE_ACKNOWLEDGE_OVER_TOLERANCE_ADMIXTURE = isFeatureEnabled('acknowledge_over_tolerance_admixture');
const FEATURE_ACKNOWLEDGE_FINISH_BATCH = isFeatureEnabled('acknowledge_finish_batch');
const FEATURE_ALLOW_BATCH_WITH_INDICATOR_ERROR = isFeatureEnabled('allow_batch_with_indicator_error');
const FEATURE_OVER_TOLERANCE_POPUP = isFeatureEnabled('over_tolerance_popup');

export const Measurement = {
  Metric: 'Metric',
  'U.S. Customary': 'USCustomary',
};

export const WaterUnit = {
  'Mix Unit': 'MixUnit',
  ga: 'GL',
  l: 'LI',
};

export const MixTrim = {
  Order: 'Order',
  Mix: 'Mix',
  Day: 'Day',
};

export const WaterAdjustBy = {
  Order: 1,
  MixDesign: 2,
  Day: 3,
};

export const MaterialDisplay = {
  Code: 'Code',
  ShortDescription: 'ShortDescription',
};

export const PumpAccumulatorDisplayType = {
  Manual: 'Manual',
  ByLoad: 'ByLoad',
};

export const RunMode = {
  Normal: 'Normal',
  NewYork: 'NewYork',
  // Minnesota: 'Minnesota',
  // California: 'California',
  // Pennsylvania: 'Pennsylvania',
};

export const ToleranceMode = {
  NetWeight: 1,
  NYDOTCumulative: 2,
};

export const PlantSettings = {
  measurement: { type: 'measurement', defaultValue: Measurement['U.S. Customary'], required: true },
  locationName: { label: 'Plant Location', type: 'text', disabled: true },
  longitude: { label: 'Longitude', type: 'text', disabled: true, hidden: true },
  latitude: { label: 'Latitude', type: 'text', disabled: true, hidden: true },
  ianaTimeZone: { type: 'text', label: 'Time Zone', disabled: true },
  maximumBatchSize: { type: 'number', min: 1, max: 100, step: 0.5, defaultValue: 10, required: true },
  maximumDropSize: { type: 'number', min: 1, max: 20, step: 0.5, defaultValue: 0, required: true },
  maximumDurationForDryUp: { type: 'number', min: 1, max: 60, step: 1, defaultValue: 30, label: 'Dry Up Loads Retention', unit: 'minutes', tooltip: 'Maximum time in minutes to retain batch history for dry up operations' },
  defaultDischargeSequenceId: { label: 'Default Discharge Sequence', type: 'sequences' },
  defaultDischargeRate: { type: 'number', min: 1, max: 1000, defaultValue: 250 },
  smallLoadSize: { type: 'number', min: 0 },
  maxQueueSize: { type: 'number', min: 1, max: 10, defaultValue: 5, required: true },
  batchEditAdmixtureType: { type: 'text', hidden: true },
  saveMixDesignBy: { type: 'mixTrim', label: 'Save Mix Design Trims/Qty by' },
  saveWaterAdjustBy: { type: 'waterAdjustBy' },
  materialDisplay: { type: 'materialDisplay', label: 'Material Display', defaultValue: MaterialDisplay.Code },
  batchUID: { type: 'text', label: 'PAC UID' },
  proxyAddress: { type: 'text' },
  proxyPort: { type: 'number', max: 65535, defaultValue: 0 },
  proxyWSPort: { type: 'number', max: 65535, defaultValue: 0 },
  batchInfoFrequency : { type: 'number', defaultValue: 5, unit: '/sec' },
  waterWeightVolume: { type: 'number', defaultValue: 3.785411784, unit: 'kg' },
  dischargeAuthorizationInput: { type: 'number' },
  runMode: { type: 'runMode' },
  toleranceMode: { type: 'toleranceMode' },
  dotPlantCode: { type: 'text', label: 'DOT Plant Code' },
  ntep: { type: 'text', label: 'NTEP CC' },
  slumpTableId: { type: 'slumpTables', label: 'Slump Table' },
  pumpAccumulatorDisplayType: { type: 'pumpAccumulatorDisplayType', label: 'Pump Accumulator Display Type', defaultValue: PumpAccumulatorDisplayType.Manual, tooltip: 'Manual – Shows the amount pumped since last login or last counter clear event. By Load – Shows the total pumped during the current load (including added water)' },
  manualFeed: { type: 'boolean', label: 'Enable Manual Feed' },
  recordManualBatchToTicket: 'boolean',
  batchClock: 'boolean',
  enableToggleMaterialVisibility: { type: 'boolean', label: 'Enable Auto Toggle Batch Material' },
  localDispatchOrderChange: { type: 'boolean', label: 'Enable Mix Design Constituent Change' },
  disableDragging: { type: 'boolean' },
  audibleOnTicketArrival: { type: 'boolean' },
  enableQuickDispatchTicket: { type: 'boolean' },
  keepSchedulerOpen: { type: 'boolean' },
  enableDemo: { type: 'boolean' },
  reduceDemoInventory: { type: 'boolean' },
  reduceMaintenanceModeInventory: { type: 'boolean' },
  useBatchCodes: { type: 'boolean' },
  holdScalesClosed: { type: 'boolean' },
  onlyJogOnLastDrop: { type: 'boolean' },
  batchInfoPermanent: { type: 'boolean', label: 'Display Last Load' },
  ignoreTare: { type: 'boolean' },
  autoStartTare: { type: 'boolean' },
  useBay: { type: 'boolean', defaultValue: false },
  enableTurboTicket: { type: 'boolean', defaultValue: false, label: 'Turbo Batch Next Ticket' },
  waterAdjustByLoad: { type: 'boolean' },
  washoutAmount: { type: 'number', hidden: true },
  keepBeltOffForPreloading: { type: 'boolean' },
  updateMixDesignForExternalOrder: { type: 'boolean' },
  disallowBatchIfConstituentIsMissing: { type: 'boolean' },
  roundOffMetered: { type: 'boolean', label: 'Round Off Metered Materials' },
  showContaminationAlert: { type: 'boolean' },
  acknowledgeOverToleranceAdmixture: { type: 'boolean', hidden: !FEATURE_ACKNOWLEDGE_OVER_TOLERANCE_ADMIXTURE },
  acknowledgeCompleteLoads: { type: 'boolean', hidden: !FEATURE_ACKNOWLEDGE_FINISH_BATCH },
  allowBatchWithScaleIndicatorError: { type: 'boolean', hidden: !FEATURE_ALLOW_BATCH_WITH_INDICATOR_ERROR },
  showPopupOnOverweight: { type: 'boolean', label: 'Show Popup When Over Tolerance', hidden: !FEATURE_OVER_TOLERANCE_POPUP },
};

export const PrintSettings = {
  printWithoutWeightsTemplateId: { label: 'Template Without Weights', type: 'ticketTemplates' },
  printWithWeightsTemplateId: { label: 'Template With Weights', type: 'ticketTemplates' },
  printOnlyWeightsTemplateId: { label: 'Template Only Weights', type: 'ticketTemplates' },
  termsAndConditionsTemplateId: {
    label: 'Template Terms & Conditions',
    type: 'ticketTemplates',
    uniqueFrom: [
      'printWithoutWeightsTemplateId',
      'printWithWeightsTemplateId',
      'printOnlyWeightsTemplateId',
    ],
  },
  printer: { type: 'printers' },
  numberOfTicketCopies: { label: 'Number of Ticket Prints', type: 'number', min: 1, max: 10 },
  printWithoutWeights: { label: 'Print Ticket at the Beginning', type: 'boolean', inverseOf: ['printWithWeights', 'printWithoutWeightsAtEnd'] },
  printWithoutWeightsAtEnd: { label: 'Print Ticket Without Weights at the End', type: 'boolean', inverseOf: ['printWithWeights', 'printWithoutWeights'] },
  printWithWeights: { label: 'Print Ticket With Weights', type: 'boolean', inverseOf: ['printWithoutWeights', 'printWithoutWeightsAtEnd'] },
  printOnlyWeights: { label: 'Print Weights', type: 'boolean' },
  printAsSoonAsMaterialsAreWeighed: { type: 'boolean' },
  autoPrint: { type: 'boolean' },
  duplexPrinting: { type: 'boolean' },
  disableLoadPrint: { type: 'boolean' },
};

export const TicketSettings = {
  useTicketNumber: { type: 'boolean' },
};

export const Configuration = {
  ...PlantSettings,
  ...PrintSettings,
  ...TicketSettings,
};
